import { useTranslation } from 'react-i18next';
import type { SmsSettings, TwilioSetupView, TwilioTakeover, TwilioTakeoverOption } from '@shared/types/index.ts';
import { formatPhoneForDisplay } from '@shared/phone.ts';
import { Alert, Button } from '../ui.tsx';

/**
 * How BlastoClean set up the owner's own Twilio number, on the own-account
 * card: "Texting set up on +1… ✓", "Calls set up ✓", or "Needs attention" with
 * the reason and, when replies go to another app now, the choices the owner
 * has to make before BlastoClean takes them over. The webhook addresses stay
 * behind a disclosure, for reference only: nothing has to be pasted.
 */
/**
 * Whether the owner's own Twilio number is waiting on them: the states the
 * card below words as "Needs attention" or asks them to confirm. Texting or
 * calls set up, or not set up yet, are not.
 */
export function twilioSetupNeedsAttention(setup: TwilioSetupView | null | undefined): boolean {
  if (!setup) return false;
  return (
    setup.status === 'needs_attention' ||
    setup.status === 'needs_confirmation' ||
    setup.calls?.status === 'needs_attention'
  );
}

export default function TwilioSetupStatus({
  settings,
  busy,
  onSetUp,
  onCopy,
  copied,
}: {
  settings: SmsSettings;
  busy: boolean;
  /** "Set up Twilio again", or agreeing to one of the takeovers offered. */
  onSetUp: (confirm?: { option: TwilioTakeoverOption; target: string }) => void;
  onCopy: (url: string) => void;
  copied: string | null;
}) {
  const { t } = useTranslation();
  const setup = settings.twilioSetup;
  if (!setup) return null;
  const rawNumber = setup.number ?? settings.fromNumber;
  const number = rawNumber ? formatPhoneForDisplay(rawNumber) : t('smsSettings.setup.yourNumber');

  return (
    <div className="mb-4 border-t border-[color:var(--color-line)] pt-4" data-testid="twilio-setup">
      <p className="mb-1 text-sm font-semibold">{t('smsSettings.setup.heading')}</p>
      <p className="mb-2 text-sm text-[color:var(--color-muted)]">{t('smsSettings.setup.description')}</p>

      <TextingStatus setup={setup} number={number} busy={busy} onSetUp={onSetUp} />
      <CallsStatus setup={setup} />

      <div className="mb-3">
        <Button type="button" busy={busy} onClick={() => onSetUp()}>
          {t('smsSettings.setup.again')}
        </Button>
        <p className="mt-1 text-xs text-[color:var(--color-muted)]">{t('smsSettings.setup.againHint')}</p>
      </div>

      <details className="text-sm">
        <summary className="cursor-pointer font-semibold">{t('smsSettings.setup.addressesSummary')}</summary>
        <p className="mt-2 text-[color:var(--color-muted)]">{t('smsSettings.setup.addressesNote')}</p>
        {[
          { label: t('smsSettings.inboundUrlLabel'), url: settings.inboundWebhookUrl },
          { label: t('smsSettings.statusUrlLabel'), url: settings.statusCallbackUrl },
        ].map((entry) => (
          <div key={entry.url} className="mt-2">
            <p className="font-semibold">{entry.label}</p>
            <p className="mb-1 break-all font-mono text-xs">{entry.url}</p>
            <Button type="button" onClick={() => onCopy(entry.url)}>
              {copied === entry.url ? t('common.copied') : t('smsSettings.copyUrl')}
            </Button>
          </div>
        ))}
      </details>
    </div>
  );
}

function TextingStatus({
  setup,
  number,
  busy,
  onSetUp,
}: {
  setup: TwilioSetupView;
  number: string;
  busy: boolean;
  onSetUp: (confirm?: { option: TwilioTakeoverOption; target: string }) => void;
}) {
  const { t } = useTranslation();
  const service = setup.messagingServiceSid ?? '';

  if (setup.status === null) {
    return <p className="mb-3 text-sm">{t('smsSettings.setup.notYet')}</p>;
  }

  if (setup.status === 'configured') {
    return (
      <div className="mb-3 text-sm" role="status">
        <p className="font-semibold">{t('smsSettings.setup.textingDone', { number })}</p>
        <p className="text-[color:var(--color-muted)]">
          {/* Texting is set up in a Messaging Service BlastoClean created, or in the owner's own one they agreed to. */}
          {setup.messagingServiceOwned
            ? `${t('smsSettings.setup.viaOwnService', { service })} ${t('smsSettings.setup.ownServiceA2p')}`
            : t('smsSettings.setup.viaAdoptedService', { service })}
        </p>
      </div>
    );
  }

  const takeover = setup.takeover;
  if (takeover?.options.includes('use_service')) {
    return <UseServiceConfirmation takeover={takeover} number={number} busy={busy} onSetUp={onSetUp} />;
  }
  const host = takeover?.previousHost ?? t('smsSettings.setup.anotherApp');
  const reason = t(`smsSettings.setup.reason.${setup.reason ?? 'setup_failed'}`, { number, service, host });

  return (
    <div className="mb-3">
      <Alert tone="warning" title={t('smsSettings.setup.needsAttention')}>
        <p>{reason}</p>
        {setup.twilioDetail ? (
          <p className="mt-1">{t('smsSettings.setup.twilioSaid', { detail: setup.twilioDetail })}</p>
        ) : null}
        {takeover ? (
          <>
            {takeover.previousFallbackHost ? (
              <p className="mt-1">
                {t('smsSettings.setup.fallbackElsewhere', { host: takeover.previousFallbackHost })}
              </p>
            ) : null}
            <p className="mt-1">{t('smsSettings.setup.untilYouChoose')}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {takeover.options.map((option) => (
                <Button
                  key={option}
                  type="button"
                  variant="primary"
                  busy={busy}
                  onClick={() => onSetUp({ option, target: takeover.target })}
                >
                  {t(`smsSettings.setup.option.${option}`)}
                </Button>
              ))}
            </div>
          </>
        ) : null}
      </Alert>
    </div>
  );
}

/**
 * "Use this Messaging Service?": the owner entered a Messaging Service of their
 * own (often the one their A2P campaign is approved on). Says what agreeing
 * changes on it, by host only, before BlastoClean writes anything there.
 */
function UseServiceConfirmation({
  takeover,
  number,
  busy,
  onSetUp,
}: {
  takeover: TwilioTakeover;
  number: string;
  busy: boolean;
  onSetUp: (confirm?: { option: TwilioTakeoverOption; target: string }) => void;
}) {
  const { t } = useTranslation();
  const service = takeover.serviceSid ?? '';
  const none = t('smsSettings.setup.useService.none');
  return (
    <div className="mb-3">
      <Alert tone="warning" title={t('smsSettings.setup.useService.title')}>
        <p>{t('smsSettings.setup.useService.intro', { service })}</p>
        <ul className="mt-1 list-disc pl-5">
          <li>{t('smsSettings.setup.useService.inbound', { host: takeover.previousHost ?? none })}</li>
          <li>{t('smsSettings.setup.useService.status', { host: takeover.previousStatusHost ?? none })}</li>
          {takeover.previousFallbackHost ? (
            <li>{t('smsSettings.setup.useService.fallback', { host: takeover.previousFallbackHost })}</li>
          ) : null}
          <li>{t('smsSettings.setup.useService.stopHelp')}</li>
          {takeover.addsNumber ? <li>{t('smsSettings.setup.useService.addsNumber', { number })}</li> : null}
        </ul>
        <p className="mt-1">{t('smsSettings.setup.useService.neverDeleted')}</p>
        <div className="mt-2">
          <Button
            type="button"
            variant="primary"
            busy={busy}
            onClick={() => onSetUp({ option: 'use_service', target: takeover.target })}
          >
            {t('smsSettings.setup.option.use_service')}
          </Button>
        </div>
      </Alert>
    </div>
  );
}

function CallsStatus({ setup }: { setup: TwilioSetupView }) {
  const { t } = useTranslation();
  const calls = setup.calls;
  if (!calls) return null;
  const number = calls.number ? formatPhoneForDisplay(calls.number) : t('smsSettings.setup.yourNumber');
  if (calls.status === 'configured')
    return (
      <p className="mb-3 text-sm font-semibold" role="status">
        {t('smsSettings.setup.callsDone', { number })}
      </p>
    );
  if (calls.status === 'not_set_up') return <p className="mb-3 text-sm">{t('smsSettings.setup.callsNotYet')}</p>;
  return (
    <div className="mb-3">
      <Alert tone="warning" title={t('smsSettings.setup.callsNeedAttention')}>
        {calls.reason ? t(`voiceSettings.callLineProblemReason.${calls.reason}`) : t('smsSettings.setup.callsNotYet')}
      </Alert>
    </div>
  );
}
